import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Button } from 'reactstrap';
import { toast } from 'react-toastify';
import { handleAddModifyOperation } from '../../../../Api/ApiMethods';
import PropTypes from 'prop-types';

const ReportTypeDataModal = ({ apiEndpoint, isOpen, toggle, mode, selectedReportType, refreshData }) => {
    const initialReportTypeData = {
        id: '',
        title: '',
        file: null, // For the new file to be uploaded
        isExpertReport: false, // Default to false (Sigorta Dilekçesi)
    };

    const [reportTypeData, setReportTypeData] = useState(initialReportTypeData);
    const [errors, setErrors] = useState({});
    const [currentFileName, setCurrentFileName] = useState(''); // To display existing file name in modify mode

    useEffect(() => {
        if (isOpen) {
            if (mode === 'modify' && selectedReportType) {
                setReportTypeData({
                    id: selectedReportType.id || '',
                    title: selectedReportType.title || '',
                    file: null, // Reset file input
                    isExpertReport: selectedReportType.isExpertReport || false,
                });
                setCurrentFileName(selectedReportType.fileName || (selectedReportType.filePath ? selectedReportType.filePath.split('/').pop() : ''));
            } else {
                setReportTypeData(initialReportTypeData);
                setCurrentFileName('');
            }
            setErrors({});
        }
    }, [mode, selectedReportType, isOpen]);

    const resetForm = () => {
        setReportTypeData(initialReportTypeData);
        setCurrentFileName('');
        setErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        if (name === 'title' && typeof value === 'string') {
            processedValue = value.toLocaleUpperCase('tr-TR');
        }

        setReportTypeData(prevData => ({ ...prevData, [name]: processedValue }));
        if (errors[name]) {
            setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setReportTypeData(prevData => ({ ...prevData, file: file }));
            if (errors.file) {
                setErrors(prevErrors => ({ ...prevErrors, file: '' }));
            }
        }
    };

    const validateForm = () => {
        const validationErrors = {};
        if (!reportTypeData.title.trim()) {
            validationErrors.title = 'Başlık zorunludur.';
        }
        if (mode === 'add' && !reportTypeData.file) {
            validationErrors.file = 'Dosya seçmek zorunludur.';
        }
        // In modify mode, file is optional (if they don't want to change it)
        // but if a new file is selected, it should be validated (e.g., type, size if needed)

        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    const handleCancel = () => {
        resetForm();
        toggle();
    };

    const handleOperation = async () => {
        if (!validateForm()) {
            toast.error('Lütfen formdaki hataları düzeltin.');
            return;
        }

        const formData = new FormData();
        formData.append('title', reportTypeData.title);
        formData.append('isExpertReport', reportTypeData.isExpertReport === true ? 'true' : 'false');

        if (reportTypeData.file) { // If a new file is selected (for add or modify)
            formData.append('File', reportTypeData.file, reportTypeData.file.name); // Ensure key is 'File' as per requirement
        }
        
        let targetApiEndpoint = apiEndpoint;
        let operationMode = mode;

        if (mode === 'modify') {
            formData.append('id', reportTypeData.id);
             // If no new file is selected in modify mode, the backend should not expect 'File'
             // or handle it gracefully. If 'File' must always be present for PUT, 
             // this logic might need adjustment or backend should handle optional file update.
             // For now, we only append if a new file is explicitly chosen.
        }


        try {
            await handleAddModifyOperation({
                apiEndpoint: targetApiEndpoint,
                selectedItem: mode === 'modify' ? { id: reportTypeData.id } : null,
                mode: operationMode,
                data: formData,
                refreshDataCallback: refreshData,
                componentName: 'Rapor Şablonu',
                isFormData: true,
            });
            toggle(); // Close modal on success
        } catch (error) {
            console.error("Rapor Şablonu operasyonu başarısız:", error);
            // Error toast is likely handled within handleAddModifyOperation
        }
    };

    const getModalTitle = () => (mode === 'add' ? 'Yeni Rapor Şablonu Ekle' : 'Rapor Şablonu Düzenle');
    const getButtonLabel = () => (mode === 'add' ? 'Ekle' : 'Güncelle');

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} backdrop="static">
            <ModalHeader toggle={handleCancel}>{getModalTitle()}</ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup>
                        <Label for="title">Başlık <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            name="title"
                            id="title"
                            value={reportTypeData.title}
                            onChange={handleInputChange}
                            placeholder="Şablon başlığı girin"
                            invalid={!!errors.title}
                        />
                        <FormFeedback>{errors.title}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="file">
                            Dosya {mode === 'add' && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="file"
                            name="file"
                            id="file"
                            onChange={handleFileChange}
                            invalid={!!errors.file}
                            // Consider adding 'accept' attribute for specific file types, e.g., accept=".docx, .pdf"
                        />
                        {mode === 'modify' && currentFileName && (
                            <small className="form-text text-muted">Mevcut dosya: {currentFileName}. Değiştirmek için yeni bir dosya seçin.</small>
                        )}
                        <FormFeedback>{errors.file}</FormFeedback>
                    </FormGroup>
                    
                    {/* Rapor Türü */}
                    <FormGroup className="d-flex align-items-center mb-3">
                        <Label htmlFor="isExpertReport" className="me-3 mb-0" style={{ flexShrink: 0, minWidth: '120px' }}>Rapor Türü</Label>
                        <div className="d-flex flex-grow-1">
                            <div className="form-check form-check-inline radio radio-success mt-2 me-3">
                                <Input
                                    className="form-check-input"
                                    id="isExpertReportTrue"
                                    type="radio"
                                    name="isExpertReport"
                                    checked={reportTypeData.isExpertReport === true}
                                    onChange={() => handleInputChange({ target: { name: 'isExpertReport', value: true, type: 'radio' } })}
                                />
                                <Label className="form-check-label" htmlFor="isExpertReportTrue">Eksper Raporu</Label>
                            </div>
                            <div className="form-check form-check-inline radio radio-danger mt-2">
                                <Input
                                    className="form-check-input"
                                    id="isExpertReportFalse"
                                    type="radio"
                                    name="isExpertReport"
                                    checked={reportTypeData.isExpertReport === false}
                                    onChange={() => handleInputChange({ target: { name: 'isExpertReport', value: false, type: 'radio' } })}
                                />
                                <Label className="form-check-label" htmlFor="isExpertReportFalse">Sigorta Dilekçesi</Label>
                            </div>
                        </div>
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={handleOperation}>{getButtonLabel()}</Button>
                <Button color="secondary" onClick={handleCancel} className="ms-2">İptal</Button>
            </ModalFooter>
        </Modal>
    );
};

ReportTypeDataModal.propTypes = {
    apiEndpoint: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['add', 'modify']).isRequired,
    selectedReportType: PropTypes.object,
    refreshData: PropTypes.func.isRequired,
};

export default ReportTypeDataModal;
